// src/features/calendar/EventDetailRail.tsx
// Event detail panel shown next to the grid — clicking an event must not lose
// the week context. Hosts RSVP, attendee management, move/duplicate/cancel/
// restore/delete and recurring-series actions.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useCalendarEvent, useI18n } from '@react/hooks';
import { authStore } from '@core/stores';
import { Button, Badge, Alert } from '@react/components';
import { Select } from '@/components/Select';
import { UserPicker } from '@/components/UserPicker';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useScope } from '@/hooks/useScope';
import { GetRecurringEventInstances } from '@core/api/endpoints/calendar/get_recurring_event_instances';
import type {
  AttendeeRole,
  AttendeeStatus,
  Calendar,
  CalendarEvent,
} from '@core/types/calendar';
import {
  addDays,
  dateTimeToIso,
  errMessage,
  fmtDateTime,
  fmtTime,
  isRecurring,
  makeEntity,
  recurrenceFrequencyKey,
  toDateInputValue,
  toTimeInputValue,
} from './time';

interface EventDetailRailProps {
  eventId: string;
  calendars: Calendar[];
  onClose: () => void;
  onEdit: (event: CalendarEvent) => void;
  /** Called after any mutation so the grid can refresh. */
  onChanged: () => void;
}

const statusBadgeVariant = (s: AttendeeStatus | undefined): 'success' | 'error' | 'warning' | 'default' => {
  switch (s) {
    case 'ATTENDEE_STATUS_ACCEPTED': return 'success';
    case 'ATTENDEE_STATUS_DECLINED': return 'error';
    case 'ATTENDEE_STATUS_TENTATIVE': return 'warning';
    default: return 'default';
  }
};

export const EventDetailRail = observer(({ eventId, calendars, onClose, onEdit, onChanged }: EventDetailRailProps) => {
  const { t, locale } = useI18n();
  const scope = useScope();
  const store = useCalendarEvent();

  const [event, setEvent] = useState<CalendarEvent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // sub-forms
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showMove, setShowMove] = useState(false);
  const [moveDate, setMoveDate] = useState('');
  const [moveTime, setMoveTime] = useState('');
  const [moveCalendarId, setMoveCalendarId] = useState('');
  const [showPropose, setShowPropose] = useState(false);
  const [proposeDate, setProposeDate] = useState('');
  const [proposeTime, setProposeTime] = useState('');
  const [proposeComment, setProposeComment] = useState('');
  const [inviteRole, setInviteRole] = useState<AttendeeRole>('ATTENDEE_ROLE_REQUIRED');
  const [instances, setInstances] = useState<CalendarEvent[]>([]);
  const [confirmOccurrence, setConfirmOccurrence] = useState<string | null>(null);
  const [rsvpDone, setRsvpDone] = useState(false);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const ev = await store.GetEvent(eventId);
      setEvent(ev);
      if (isRecurring(ev)) {
        const seriesId = ev.recurring_event_id?.id ?? ev.entity?.id ?? eventId;
        const res = await GetRecurringEventInstances(seriesId, {
          time_min: new Date().toISOString(),
          time_max: addDays(new Date(), 90).toISOString(),
        });
        setInstances(res.events?.events ?? []);
      } else {
        setInstances([]);
      }
    } catch (e) {
      setError(errMessage(e, t('calendar.event.loadError')));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setRsvpDone(false);
    setShowCancel(false);
    setShowMove(false);
    setShowPropose(false);
    load();
  }, [eventId]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      onChanged();
    } catch (e) {
      setError(errMessage(e, t('calendar.event.actionError')));
    } finally {
      setBusy(false);
    }
  };

  const myProfileId = authStore.activeProfileId;
  const attendees = event?.attendees?.attendees ?? [];
  const me = attendees.find((a) => a.attendee?.entity?.id === myProfileId);
  const isCancelled = event?.status === 'EVENT_STATUS_CANCELLED';

  const rsvp = (status: AttendeeStatus) =>
    run(async () => {
      await store.RespondToInvitation(eventId, {
        event: makeEntity(eventId, 'calendar_event', scope),
        status,
      });
      setRsvpDone(true);
    });

  if (isLoading && !event) {
    return (
      <aside className="w-full shrink-0 space-y-3 border-l border-border p-4 lg:w-96" aria-busy="true">
        <div className="h-6 w-2/3 animate-pulse rounded bg-muted" />
        <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
        <div className="h-32 animate-pulse rounded bg-muted" />
      </aside>
    );
  }

  return (
    <aside className="w-full shrink-0 space-y-4 overflow-y-auto border-l border-border p-4 lg:w-96">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-foreground">{event?.title || t('calendar.event.untitled')}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {event?.is_all_day
              ? `${fmtDateTime(event?.start_time, locale).split(',')[0]} · ${t('calendar.event.allDay')}`
              : `${fmtDateTime(event?.start_time, locale)} – ${fmtTime(event?.end_time, locale)}`}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {isCancelled && <Badge variant="error">{t('calendar.event.cancelled')}</Badge>}
        {event && isRecurring(event) && (
          <Badge variant="default">
            {t('calendar.series.badge')}
            {recurrenceFrequencyKey(event.recurrence_rule) && (
              <> · {t(`calendar.recurrence.${recurrenceFrequencyKey(event.recurrence_rule)}`)}</>
            )}
          </Badge>
        )}
        {event?.location?.display_name && (
          <span className="text-xs text-muted-foreground">{event.location.display_name}</span>
        )}
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {event?.conference?.uri && (
        <a
          href={event.conference.uri}
          target="_blank"
          rel="noreferrer"
          className="block truncate text-sm text-primary hover:underline"
        >
          {t('calendar.event.joinConference')}
        </a>
      )}

      {event?.description && (
        <p className="whitespace-pre-wrap text-sm text-foreground">{event.description}</p>
      )}

      {/* RSVP — what did I decide about attending? */}
      {me && !isCancelled && (
        <div className="space-y-2 rounded-md border border-border p-3">
          <span className="block text-sm font-medium text-foreground">{t('calendar.rsvp.question')}</span>
          {rsvpDone && <Alert variant="success">{t('calendar.rsvp.saved')}</Alert>}
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant={me.status === 'ATTENDEE_STATUS_ACCEPTED' ? 'primary' : 'secondary'} disabled={busy} onClick={() => rsvp('ATTENDEE_STATUS_ACCEPTED')}>
              {t('calendar.rsvp.accept')}
            </Button>
            <Button size="sm" variant={me.status === 'ATTENDEE_STATUS_TENTATIVE' ? 'primary' : 'secondary'} disabled={busy} onClick={() => rsvp('ATTENDEE_STATUS_TENTATIVE')}>
              {t('calendar.rsvp.tentative')}
            </Button>
            <Button size="sm" variant={me.status === 'ATTENDEE_STATUS_DECLINED' ? 'primary' : 'secondary'} disabled={busy} onClick={() => rsvp('ATTENDEE_STATUS_DECLINED')}>
              {t('calendar.rsvp.decline')}
            </Button>
          </div>
          <button
            type="button"
            onClick={() => {
              const s = event?.start_time ? new Date(event.start_time) : new Date();
              setProposeDate(toDateInputValue(s));
              setProposeTime(toTimeInputValue(s));
              setShowPropose((v) => !v);
            }}
            aria-expanded={showPropose}
            className="text-sm text-primary hover:underline"
          >
            {t('calendar.rsvp.proposeNewTime')}
          </button>
          {showPropose && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="date"
                  aria-label={t('calendar.event.startDate')}
                  value={proposeDate}
                  onChange={(e) => setProposeDate(e.target.value)}
                  className="rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
                />
                <input
                  type="time"
                  aria-label={t('calendar.event.startTime')}
                  value={proposeTime}
                  onChange={(e) => setProposeTime(e.target.value)}
                  className="rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
                />
              </div>
              <input
                type="text"
                aria-label={t('calendar.rsvp.comment')}
                placeholder={t('calendar.rsvp.comment')}
                value={proposeComment}
                onChange={(e) => setProposeComment(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
              />
              <Button
                size="sm"
                variant="secondary"
                disabled={busy || !proposeDate}
                onClick={() =>
                  run(async () => {
                    const start = dateTimeToIso(proposeDate, proposeTime);
                    const durMs = event?.start_time && event?.end_time
                      ? new Date(event.end_time).getTime() - new Date(event.start_time).getTime()
                      : 3600000;
                    await store.ProposeNewTime(eventId, {
                      event: makeEntity(eventId, 'calendar_event', scope),
                      proposed_start: start,
                      proposed_end: new Date(new Date(start).getTime() + durMs).toISOString(),
                      comment: proposeComment.trim() || undefined,
                    });
                    setShowPropose(false);
                    setProposeComment('');
                  })
                }
              >
                {t('calendar.rsvp.sendProposal')}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-1.5">
        {event && !isCancelled && (
          <Button size="sm" variant="secondary" onClick={() => onEdit(event)}>
            {t('common.edit')}
          </Button>
        )}
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() =>
            run(() =>
              store.DuplicateEvent(eventId, {
                event: makeEntity(eventId, 'calendar_event', scope),
              }),
            )
          }
        >
          {t('common.duplicate')}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            const s = event?.start_time ? new Date(event.start_time) : new Date();
            setMoveDate(toDateInputValue(s));
            setMoveTime(toTimeInputValue(s));
            setMoveCalendarId(event?.calendar?.id ?? '');
            setShowMove((v) => !v);
          }}
          aria-expanded={showMove}
        >
          {t('calendar.event.move')}
        </Button>
        {!isCancelled && (
          <Button size="sm" variant="secondary" onClick={() => setShowCancel((v) => !v)} aria-expanded={showCancel}>
            {t('calendar.event.cancel')}
          </Button>
        )}
        {isCancelled && (
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() =>
              run(() =>
                store.RestoreEvent(eventId, {
                  event: makeEntity(eventId, 'calendar_event', scope),
                  notify_attendees: true,
                }),
              )
            }
          >
            {t('calendar.event.restore')}
          </Button>
        )}
        <Button size="sm" variant="danger" onClick={() => setConfirmDelete(true)}>
          {t('common.delete')}
        </Button>
      </div>

      {showMove && (
        <div className="space-y-2 rounded-md border border-border p-3">
          <span className="block text-sm font-medium text-foreground">{t('calendar.event.moveTo')}</span>
          <div className="flex gap-2">
            <input
              type="date"
              aria-label={t('calendar.event.startDate')}
              value={moveDate}
              onChange={(e) => setMoveDate(e.target.value)}
              className="rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
            />
            <input
              type="time"
              aria-label={t('calendar.event.startTime')}
              value={moveTime}
              onChange={(e) => setMoveTime(e.target.value)}
              className="rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
            />
          </div>
          <Select
            aria-label={t('calendar.event.calendar')}
            value={moveCalendarId}
            onChange={(e) => setMoveCalendarId(e.target.value)}
            options={calendars.map((c) => ({ value: c.entity?.id ?? '', label: c.name }))}
          />
          <Button
            size="sm"
            variant="secondary"
            disabled={busy || !moveDate}
            onClick={() =>
              run(async () => {
                const start = dateTimeToIso(moveDate, moveTime);
                const durMs = event?.start_time && event?.end_time
                  ? new Date(event.end_time).getTime() - new Date(event.start_time).getTime()
                  : 3600000;
                await store.MoveEvent(eventId, {
                  event: makeEntity(eventId, 'calendar_event', scope),
                  new_start_time: start,
                  new_end_time: new Date(new Date(start).getTime() + durMs).toISOString(),
                  target_calendar:
                    moveCalendarId && moveCalendarId !== event?.calendar?.id
                      ? makeEntity(moveCalendarId, 'calendar', scope)
                      : undefined,
                  notify_attendees: true,
                });
                setShowMove(false);
              })
            }
          >
            {t('calendar.event.move')}
          </Button>
        </div>
      )}

      {showCancel && (
        <div className="space-y-2 rounded-md border border-border p-3">
          <span className="block text-sm font-medium text-foreground">{t('calendar.event.cancelTitle')}</span>
          <input
            type="text"
            aria-label={t('calendar.event.cancelReason')}
            placeholder={t('calendar.event.cancelReason')}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
          />
          <Button
            size="sm"
            variant="danger"
            disabled={busy}
            onClick={() =>
              run(async () => {
                await store.CancelEvent(eventId, {
                  event: makeEntity(eventId, 'calendar_event', scope),
                  reason: cancelReason.trim() || undefined,
                  notify_attendees: true,
                });
                setShowCancel(false);
                setCancelReason('');
              })
            }
          >
            {t('calendar.event.cancelConfirm')}
          </Button>
        </div>
      )}

      {/* Attendees */}
      <div className="space-y-2">
        <span className="block text-sm font-medium text-foreground">
          {t('calendar.event.attendees')} {attendees.length > 0 && `(${attendees.length})`}
        </span>
        {attendees.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('calendar.event.noAttendees')}</p>
        )}
        <ul className="space-y-1">
          {attendees.map((a) => (
            <li key={a.entity?.id ?? a.attendee?.entity?.id} className="flex items-center justify-between gap-2 text-sm text-foreground">
              <span className="truncate">
                {a.attendee?.display_name || a.attendee?.entity?.id}
                {a.is_organizer && <span className="ml-1 text-xs text-muted-foreground">({t('calendar.event.organizer')})</span>}
              </span>
              <span className="flex items-center gap-1">
                <Badge variant={statusBadgeVariant(a.status)}>
                  {t(`calendar.attendeeStatus.${(a.status ?? 'ATTENDEE_STATUS_NEEDS_ACTION').replace('ATTENDEE_STATUS_', '').toLowerCase()}`)}
                </Badge>
                {!a.is_organizer && (
                  <button
                    type="button"
                    aria-label={t('calendar.event.removeAttendee')}
                    disabled={busy}
                    onClick={() =>
                      run(() =>
                        store.RemoveAttendee(
                          eventId,
                          a.attendee?.entity?.id ?? a.entity?.id ?? '',
                        ),
                      )
                    }
                    className="rounded-md p-0.5 text-muted-foreground hover:text-destructive"
                  >
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
        {!isCancelled && (
          <div className="space-y-1.5">
            <Select
              aria-label={t('calendar.event.inviteRole')}
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as AttendeeRole)}
              options={[
                { value: 'ATTENDEE_ROLE_REQUIRED', label: t('calendar.attendeeRole.required') },
                { value: 'ATTENDEE_ROLE_OPTIONAL', label: t('calendar.attendeeRole.optional') },
                { value: 'ATTENDEE_ROLE_CHAIR', label: t('calendar.attendeeRole.chair') },
              ]}
            />
            <UserPicker
              value=""
              placeholder={t('calendar.event.invite')}
              onChange={(userId, displayName) => {
                if (!userId) return;
                run(() =>
                  store.InviteAttendees(eventId, {
                    event: makeEntity(eventId, 'calendar_event', scope),
                    attendees: [
                      {
                        attendee: { entity: makeEntity(userId, 'profile', scope), display_name: displayName, avatar: '' },
                        role: inviteRole,
                        type: 'ATTENDEE_TYPE_INDIVIDUAL',
                        is_optional: inviteRole === 'ATTENDEE_ROLE_OPTIONAL',
                      },
                    ],
                  }),
                );
              }}
            />
          </div>
        )}
      </div>

      {/* Series */}
      {event && isRecurring(event) && (
        <div className="space-y-2">
          <span className="block text-sm font-medium text-foreground">{t('calendar.series.upcoming')}</span>
          {instances.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('calendar.series.noUpcoming')}</p>
          )}
          <ul className="space-y-1">
            {instances.slice(0, 8).map((inst, i) => (
              <li key={inst.entity?.id ?? i} className="flex items-center justify-between text-sm text-foreground">
                <span>{fmtDateTime(inst.start_time, locale)}</span>
                <button
                  type="button"
                  aria-label={t('calendar.series.deleteOccurrence')}
                  disabled={busy}
                  onClick={() => setConfirmOccurrence(inst.original_start_time ?? inst.start_time ?? null)}
                  className="rounded-md p-0.5 text-muted-foreground hover:text-destructive"
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={t('calendar.event.deleteTitle')}
        description={t('calendar.event.deleteDescription')}
        destructive
        isConfirming={busy}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() =>
          run(async () => {
            await store.DeleteEvent(eventId);
            setConfirmDelete(false);
            onClose();
          })
        }
      />
      <ConfirmDialog
        open={!!confirmOccurrence}
        title={t('calendar.series.deleteOccurrenceTitle')}
        description={t('calendar.series.deleteOccurrenceDescription')}
        destructive
        isConfirming={busy}
        onCancel={() => setConfirmOccurrence(null)}
        onConfirm={() =>
          run(async () => {
            const seriesId = event?.recurring_event_id?.id ?? eventId;
            await store.DeleteRecurringEventOccurrence(seriesId, confirmOccurrence ?? '');
            setConfirmOccurrence(null);
          })
        }
      />
    </aside>
  );
});
